/**
 * Pure helpers over the edit format. Used by the timeline UI, the compositor
 * (preview + export) and tests. No DOM dependencies.
 */
import { effectParams, paramKeyframes, transformParams } from './params';
import type {
  AnyClip,
  AudioClip,
  AudioTrack,
  Edit,
  Effect,
  OverlayClip,
  Param,
  ResolvedEffect,
  Track,
  TransitionType,
  VideoClip,
  VideoTrack,
} from './types';

const ID_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';
const ID_LENGTH = 14; // 14 base36 chars ≈ 72 random bits

/**
 * Generate a collision-safe id with a prefix ("c", "t", "a", "src_", …).
 * Ids must stay unique across documents produced by independent clients
 * (collaborative editing merges them), so this is pure CSPRNG randomness.
 */
export function genId(prefix: string): string {
  const bytes = new Uint8Array(ID_LENGTH);
  crypto.getRandomValues(bytes);
  let suffix = '';
  for (let i = 0; i < ID_LENGTH; i++) suffix += ID_ALPHABET[bytes[i] % 36];
  return prefix + suffix;
}

const CLIP_ID_PREFIX: Record<Track['kind'], string> = {
  video: 'c',
  overlay: 't',
  audio: 'a',
};

/**
 * Load-time repair for documents that predate collision-safe ids / effect ids:
 * reassigns duplicate track and clip ids, and fills in missing or duplicated
 * effect ids. Mutates in place; callers run this on a fresh clone at load.
 */
export function repairEditIds(edit: Edit): void {
  const trackIds = new Set<string>();
  for (const track of edit.tracks) {
    if (trackIds.has(track.id)) track.id = genId('trk');
    trackIds.add(track.id);
  }
  const clipIds = new Set<string>();
  for (const track of edit.tracks) {
    for (const clip of track.clips as AnyClip[]) {
      if (clipIds.has(clip.id)) clip.id = genId(CLIP_ID_PREFIX[track.kind]);
      clipIds.add(clip.id);
      if ('effects' in clip) {
        const effectIds = new Set<string>();
        for (const e of clip.effects) {
          if (!e.id || effectIds.has(e.id)) e.id = genId('fx');
          effectIds.add(e.id);
        }
      }
    }
  }
}

/** Fresh effect ids for effects cloned onto a new clip (split, paste). */
export function reassignEffectIds(effects: Effect[]): Effect[] {
  return effects.map((e) => ({ ...e, id: genId('fx') }));
}

export function emptyEdit(): Edit {
  return {
    type: 'fabled-edit',
    version: 2,
    settings: {
      width: 1280,
      height: 720,
      fps: 30,
      sampleRate: 48000,
      channels: 2,
      background: '#000000',
    },
    sources: {},
    tracks: [
      { id: 'v1', kind: 'video', label: 'V1', muted: false, clips: [] },
      { id: 'o1', kind: 'overlay', label: 'T1', muted: false, clips: [] },
      { id: 'a1', kind: 'audio', label: 'A1', muted: false, gain: 1.0, clips: [] },
    ],
  };
}

/** Timeline duration of any clip. */
export function clipDuration(clip: AnyClip): number {
  if ('kind' in clip) return clip.duration; // text | image overlay clips
  return (clip.out - clip.in) / (clip.speed ?? 1); // video | audio
}

/** Timeline end of any clip. */
export function clipEnd(clip: AnyClip): number {
  return clip.start + clipDuration(clip);
}

/** Max clip end over all tracks. */
export function totalDuration(edit: Edit): number {
  let max = 0;
  for (const track of edit.tracks) {
    for (const clip of track.clips) max = Math.max(max, clipEnd(clip));
  }
  return max;
}

/** Clips active at time t on a track (0, 1 or — during a transition — 2). */
export function activeClipsAt<T extends Track>(track: T, t: number): T['clips'][number][] {
  return track.clips.filter((c) => t >= c.start && t < clipEnd(c));
}

export interface TransitionWindow {
  a: VideoClip;
  b: VideoClip;
  type: TransitionType;
  /** window start = b.start */
  t0: number;
  /** window end = a's end */
  t1: number;
  /** 0..1 within [t0, t1] for the given time */
  progress: number;
}

/**
 * If t falls inside a transition overlap window on this video track, return it.
 * Per the format: clip B with transitionIn overlapping previous clip A over
 * [B.start, A.end], duration == overlap.
 */
export function transitionWindowAt(track: VideoTrack, t: number): TransitionWindow | null {
  const clips = track.clips;
  for (let i = 1; i < clips.length; i++) {
    const b = clips[i];
    const a = clips[i - 1];
    if (!b.transitionIn) continue;
    const aEnd = clipEnd(a);
    if (b.start < aEnd && t >= b.start && t < aEnd) {
      const d = aEnd - b.start;
      return {
        a,
        b,
        type: b.transitionIn.type,
        t0: b.start,
        t1: aEnd,
        progress: d > 0 ? (t - b.start) / d : 1,
      };
    }
  }
  return null;
}

/**
 * Fade multiplier (0..1) for a clip at timeline time t.
 * Applies fadeIn from the clip start and fadeOut into the clip end.
 */
export function fadeFactor(
  clip: { start: number; fadeIn: number; fadeOut: number },
  duration: number,
  t: number,
): number {
  const local = t - clip.start;
  if (local < 0 || local > duration) return 0;
  let f = 1;
  if (clip.fadeIn > 0) f = Math.min(f, local / clip.fadeIn);
  if (clip.fadeOut > 0) f = Math.min(f, (duration - local) / clip.fadeOut);
  return Math.max(0, Math.min(1, f));
}

/**
 * Canvas ctx.filter string per the format doc's mapping table. Takes RESOLVED
 * effects (plain numbers) — sample animated effects first via
 * `sampleEffects(effects, t)` from format/params.
 */
export function effectsToFilterString(effects: ResolvedEffect[]): string {
  if (!effects.length) return 'none';
  const parts: string[] = [];
  for (const e of effects) {
    switch (e.type) {
      case 'brightness':
        parts.push(`brightness(${round3(1 + e.amount)})`);
        break;
      case 'contrast':
        parts.push(`contrast(${round3(e.amount)})`);
        break;
      case 'saturation':
        parts.push(`saturate(${round3(e.amount)})`);
        break;
      case 'hue':
        parts.push(`hue-rotate(${round3(e.degrees)}deg)`);
        break;
      case 'grayscale':
        parts.push(`grayscale(${round3(e.amount)})`);
        break;
      case 'sepia':
        parts.push(`sepia(${round3(e.amount)})`);
        break;
      case 'invert':
        parts.push(`invert(${round3(e.amount)})`);
        break;
      case 'blur':
        parts.push(`blur(${round3(e.radius)}px)`);
        break;
    }
  }
  return parts.join(' ');
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Default parameter set for a newly added effect. */
export function defaultEffect(type: Effect['type']): Effect {
  const id = genId('fx');
  switch (type) {
    case 'brightness':
      return { id, type, amount: 0 };
    case 'contrast':
      return { id, type, amount: 1 };
    case 'saturation':
      return { id, type, amount: 1 };
    case 'hue':
      return { id, type, degrees: 0 };
    case 'grayscale':
      return { id, type, amount: 1 };
    case 'sepia':
      return { id, type, amount: 1 };
    case 'invert':
      return { id, type, amount: 1 };
    case 'blur':
      return { id, type, radius: 4 };
  }
}

/**
 * Track ids that are audible right now: if any track is soloed, exactly the
 * soloed non-muted tracks; otherwise all non-muted tracks. Solo/mute affect
 * AUDIO only (video from a muted track still renders).
 */
export function audibleTracks(edit: Edit): Set<string> {
  const anySolo = edit.tracks.some((t) => t.solo);
  const out = new Set<string>();
  for (const t of edit.tracks) {
    if (t.muted) continue;
    if (anySolo && !t.solo) continue;
    out.add(t.id);
  }
  return out;
}

export function isVideoTrack(t: Track): t is VideoTrack {
  return t.kind === 'video';
}
export function isAudioTrack(t: Track): t is AudioTrack {
  return t.kind === 'audio';
}

/**
 * Media clips are told apart by `transform`, which only video clips carry.
 * NOT by `speed` — audio clips carry that too (detached audio keeps the speed
 * of the video clip it came from), so an absence-of-speed test would
 * misclassify every sped-up audio clip.
 */
export function isVideoClip(c: AnyClip): c is VideoClip {
  return !('kind' in c) && 'transform' in c;
}
export function isAudioClip(c: AnyClip): c is AudioClip {
  return !('kind' in c) && !('transform' in c);
}
export function isOverlayClip(c: AnyClip): c is OverlayClip {
  return 'kind' in c;
}

/** Format seconds as HH:MM:SS.mmm-ish timecode (mm:ss.d below an hour). */
export function formatTime(t: number, withMillis = false): string {
  if (!isFinite(t) || t < 0) t = 0;
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = Math.floor(t % 60);
  const frac = t - Math.floor(t);
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  const base = h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  if (!withMillis) return base;
  return `${base}.${String(Math.floor(frac * 10))}`;
}

/** Format seconds as frame-accurate SMPTE-style timecode HH:MM:SS:FF. */
export function formatTimecode(t: number, fps: number): string {
  if (!isFinite(t) || t < 0) t = 0;
  const totalFrames = Math.round(t * fps);
  const fi = Math.floor(totalFrames % fps);
  const secs = Math.floor(totalFrames / fps);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(h)}:${p(m)}:${p(s)}:${p(fi)}`;
}

/** Light validation per the format doc; returns human-readable errors. */
function keyframeErrors(clipId: string, params: Param[], lo: number, hi: number): string[] {
  const errors: string[] = [];
  for (const p of params) {
    const kfs = paramKeyframes(p);
    for (let i = 0; i < kfs.length; i++) {
      if (i > 0 && kfs[i].t <= kfs[i - 1].t) {
        errors.push(`clip ${clipId}: keyframes not strictly sorted`);
        break;
      }
      if (kfs[i].t < lo - 0.001 || kfs[i].t > hi + 0.001) {
        errors.push(`clip ${clipId}: keyframe outside clip range`);
        break;
      }
    }
  }
  return errors;
}

export function validateEdit(edit: Edit): string[] {
  const errors: string[] = [];
  if (edit.type !== 'fabled-edit' || edit.version !== 2) errors.push('bad type/version');
  const s = edit.settings;
  if (!(s.fps >= 1 && s.fps <= 60)) errors.push('fps out of range 1..60');
  if (!(s.width >= 16 && s.width <= 3840 && s.height >= 16 && s.height <= 3840))
    errors.push('width/height out of range 16..3840');
  for (const track of edit.tracks) {
    const clips = track.clips as AnyClip[];
    for (let i = 0; i < clips.length; i++) {
      const c = clips[i];
      if ('source' in c && !edit.sources[c.source])
        errors.push(`clip ${c.id}: unknown source ${c.source}`);
      if ('in' in c) {
        const src = edit.sources[(c as VideoClip | AudioClip).source];
        const vc = c as VideoClip | AudioClip;
        if (!(vc.in >= 0 && vc.in < vc.out)) errors.push(`clip ${c.id}: bad in/out`);
        if (src && src.kind === 'pandora-video' && vc.out > src.duration + 0.001)
          errors.push(`clip ${c.id}: out beyond source duration`);
      }
      // keyframes: source-time axis within [in, out] for media clips,
      // clip-local [0, duration] for overlay clips
      if (isVideoClip(c)) {
        errors.push(
          ...keyframeErrors(c.id, [...effectParams(c.effects), ...transformParams(c.transform)], c.in, c.out),
        );
      } else if (isOverlayClip(c)) {
        const params =
          c.kind === 'text' ? [c.position.x, c.position.y] : transformParams(c.transform);
        errors.push(...keyframeErrors(c.id, params, 0, c.duration));
      }
      if (i > 0) {
        const prev = clips[i - 1];
        if (c.start < prev.start) errors.push(`track ${track.id}: clips not sorted`);
        const overlap = clipEnd(prev) - c.start;
        if (overlap > 0.0001) {
          if (track.kind !== 'video') {
            errors.push(`track ${track.id}: overlap on non-video track`);
          } else {
            const vclip = c as VideoClip;
            if (!vclip.transitionIn)
              errors.push(`clip ${c.id}: overlap without transitionIn`);
            else if (
              overlap - 0.001 > Math.min(clipDuration(prev), clipDuration(c))
            )
              errors.push(`clip ${c.id}: transition longer than a participating clip`);
          }
        }
      }
    }
  }
  if (totalDuration(edit) <= 0) errors.push('timeline is empty');
  return errors;
}
